import React, { useState, useEffect, useMemo } from "react";
import { FormikProvider, useFormik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CardContent,
  CardActions,
  Card,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  IconButton,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { Post, GetAll, Put, apiURL } from "../../../utils/apiCalls";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RenderRazorpay from "../agents/RenderRazorpay";

const RATE_TYPE_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

const RATE_TYPES = Object.keys(RATE_TYPE_LABELS);

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const amount = parseFloat(value);
  if (Number.isNaN(amount)) return "";
  return amount.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const normalizeRates = (rates) => {
  if (!rates) return [];

  const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") {
      if (Array.isArray(data.models)) {
        return data.models.map((model) => model.attributes || model);
      }
      if (typeof data.toJSON === "function") {
        const json = data.toJSON();
        return Array.isArray(json) ? json : [];
      }
      return Object.values(data);
    }
    return [];
  };

  return toArray(rates)
    .filter(Boolean)
    .map((rate) => ({
      ...rate,
      rate_type: rate.rate_type ? String(rate.rate_type).toLowerCase() : "",
    }))
    .filter((rate) => RATE_TYPES.includes(rate.rate_type));
};

const AdsSchema = Yup.object().shape({
  layout_request_id: Yup.string().required("Project is required"),
  position_id: Yup.string().required("Position is required"),
  name: Yup.string().required("Name is required"),
  developer_name: Yup.string().optional(),
  description: Yup.string().optional(),
  rate_type: Yup.string()
    .oneOf(RATE_TYPES, "Select a valid billing cycle")
    .required("Billing cycle is required"),
  duration_value: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Duration must be a number")
    .integer("Duration must be an integer")
    .min(1, "Duration must be at least 1")
    .required("Duration is required"),
});

// =============================
// Styled Components (matching Layout Request style)
// =============================
const UploadBox = styled(Box)(({ theme, isUploaded }) => ({
  border: isUploaded ? "2px solid #4caf50" : "2px dashed #1976d2",
  borderRadius: 12,
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: isUploaded ? "#e8f5e9" : "#f8fafc",
  transition: "0.3s",
  "&:hover": {
    backgroundColor: isUploaded ? "#d0f0d1" : "#e3f2fd",
  },
}));

const AdsCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adId = searchParams.get('id');
  const isEditMode = !!adId;
  
  const [createdAd, setCreatedAd] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [error, setError] = useState({ type: "", msg: "" });
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [layoutRequests, setLayoutRequests] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [adPositions, setAdPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [loadingAd, setLoadingAd] = useState(false);
  const [existingAd, setExistingAd] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [displayRazorpay, setDisplayRazorpay] = useState(false);
  const [pendingAdData, setPendingAdData] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdAdId, setCreatedAdId] = useState(null);

  const availableBillingRates = useMemo(
    () => normalizeRates(selectedPosition?.rates),
    [selectedPosition]
  );

  // Support images, documents, and videos
  const fileTypes = ["JPEG", "JPG", "PNG", "GIF", "WEBP", "PDF", "DOC", "DOCX", "MP4", "MOV", "AVI", "WMV", "FLV", "WEBM", "MKV"];

  // Fetch layout requests (projects)
  useEffect(() => {
    const fetchLayoutRequests = async () => {
      setLoadingProjects(true);
      try {
        const user = JSON.parse(sessionStorage.getItem('user_details') || 'null');
        const isProcurement = user?.user_type?.toLowerCase() === 'procurement';
        const endpoint = isProcurement
          ? "/api/v1/layouts/my/requests/filter?limit=1000"
          : "/api/v1/layouts/admin/requests/all?limit=1000";
        const response = await GetAll(apiURL + endpoint);
        if (response && response.data && response.data.status) {
          setLayoutRequests(response.data.data || []);
        } else {
          toast.error("Failed to load projects");
          setLayoutRequests([]);
        }
      } catch (error) {
        console.error("Error fetching layout requests:", error);
        toast.error("Error loading projects");
        setLayoutRequests([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchLayoutRequests();
  }, []);

  // Fetch ad positions from database
  useEffect(() => {
    const fetchAdPositions = async () => {
      setLoadingPositions(true);
      try {
        const response = await GetAll(apiURL + "/api/v1/ads/positions/list");
        if (response && response.data && response.data.status) {
          // Ensure data is an array
          let positions = response.data.data;
          if (!Array.isArray(positions)) {
            // If it's an object with array inside, try to extract it
            if (positions && typeof positions === 'object') {
              // Check if it's a Bookshelf collection-like object
              if (positions.models) {
                positions = positions.models.map(m => m.attributes || m);
              } else if (positions.toJSON) {
                positions = positions.toJSON();
              } else {
                // Try to convert object to array
                positions = Object.values(positions);
              }
            }
            // Final check - ensure it's an array
            if (!Array.isArray(positions)) {
              console.warn("Positions data is not an array:", positions);
              positions = [];
            }
          }
          const normalizedPositions = positions.map((position) => ({
            ...position,
            rates: normalizeRates(position?.rates),
          }));
          setAdPositions(normalizedPositions);
        } else {
          toast.error("Failed to load ad positions");
          setAdPositions([]);
        }
      } catch (error) {
        console.error("Error fetching ad positions:", error);
        toast.error("Error loading ad positions");
        setAdPositions([]);
      } finally {
        setLoadingPositions(false);
      }
    };
    fetchAdPositions();
  }, []);

  const formik = useFormik({
    initialValues: {
      layout_request_id: "",
      position_id: "",
      name: "",
      developer_name: "",
      description: "",
      rate_type: "",
      duration_value: 1,
    },
    validationSchema: AdsSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const numericDuration = parseInt(values.duration_value, 10) || 1;

      if (isEditMode && adId) {
        // Update existing ad
        const editRate = availableBillingRates.find(
          (rate) => rate.rate_type === values.rate_type
        );
        const payload = {
          ...values,
          rate_amount:
            editRate && !Number.isNaN(parseFloat(editRate.amount))
              ? parseFloat(editRate.amount)
              : null,
          duration_value: numericDuration,
        };

        const response = await Put(apiURL + "/api/v1/ads/", adId, payload);
        if (response && response.data && response.data.status) {
          const updatedAd = response.data.data;
          setCreatedAd(updatedAd);
          setExistingAd(updatedAd);
          toast.success("Ad updated successfully");
          
          // Upload any pending files
          if (uploadingFiles.length > 0) {
            await uploadPendingFiles(adId);
          }
          
          // Refresh uploads list
          await fetchAdUploads(adId);
          
          setSubmitting(false);
          // Optionally navigate back or stay on page
          // navigate('/dashboard/procurement/ads-requests');
        } else {
          toast.error(response?.data?.message || "Failed to update ad");
          setSubmitting(false);
        }
      } else {
        // For new ads, process payment first
        if (!selectedPosition) {
          toast.error("Please select an ad position");
          setSubmitting(false);
          return;
        }

        const availableRates = availableBillingRates;
        if (availableRates.length === 0) {
          toast.error("Selected position does not have any billing rates configured");
          setSubmitting(false);
          return;
        }

        const selectedRate = availableRates.find(
          (rate) => rate.rate_type === values.rate_type
        );

        if (!selectedRate) {
          toast.error("Please choose a billing cycle for this position");
          setSubmitting(false);
          return;
        }

        const amount = parseFloat(selectedRate.amount);
        if (isNaN(amount) || amount <= 0) {
          toast.error("Invalid rate amount for the selected billing cycle");
          setSubmitting(false);
          return;
        }
        const totalAmount = amount * numericDuration;
        if (isNaN(totalAmount) || totalAmount <= 0) {
          toast.error("Enter a valid duration to calculate payment");
          setSubmitting(false);
          return;
        }

        try {
          // Store ad data for after payment
          const adPayload = {
            ...values,
            rate_amount: amount,
            duration_value: numericDuration,
          };
          setPendingAdData(adPayload);
          
          // Create payment order for ad
          const paymentResponse = await Post(apiURL + "/api/v1/ads/payments/order", {
            amount: totalAmount,
            full_name: values.name || "Ad Payment",
            position_id: values.position_id,
            layout_request_id: values.layout_request_id,
            rate_type: values.rate_type,
            rate_amount: amount,
            duration_value: numericDuration,
          });

          if (paymentResponse && paymentResponse.data && paymentResponse.data.status) {
            setPaymentOrder({
              orderId: paymentResponse.data.data.order_id,
              currency: paymentResponse.data.data.currency || "INR",
              amount: paymentResponse.data.data.amount,
            });
            setDisplayRazorpay(true);
          } else {
            toast.error(paymentResponse?.data?.message || "Failed to create payment order");
            setSubmitting(false);
          }
        } catch (error) {
          console.error("Payment order error:", error);
          toast.error("Failed to initiate payment");
          setSubmitting(false);
        }
      }
    },
  });

  const { errors, touched, values, setFieldValue, isSubmitting, handleSubmit, getFieldProps } = formik;

  const selectedBillingRate = useMemo(() => {
    if (!values?.rate_type) return null;
    return (
      availableBillingRates.find(
        (rate) => rate.rate_type === values.rate_type
      ) || null
    );
  }, [availableBillingRates, values?.rate_type]);

  const calculatedTotalAmount = useMemo(() => {
    if (!selectedBillingRate) return 0;
    const duration = parseInt(values?.duration_value, 10) || 0;
    const rateAmount = parseFloat(selectedBillingRate.amount || 0);
    if (!duration || !rateAmount || Number.isNaN(rateAmount)) return 0;
    return rateAmount * duration;
  }, [selectedBillingRate, values?.duration_value]);

  useEffect(() => {
    if (!selectedPosition) {
      if (values.rate_type) {
        setFieldValue("rate_type", "");
      }
      if (values.duration_value !== 1) {
        setFieldValue("duration_value", 1);
      }
      return;
    }

    if (availableBillingRates.length === 0) {
      if (values.rate_type) {
        setFieldValue("rate_type", "");
      }
      if (values.duration_value !== 1) {
        setFieldValue("duration_value", 1);
      }
      return;
    }

    const hasCurrentSelection = availableBillingRates.some(
      (rate) => rate.rate_type === values.rate_type
    );

    if (!hasCurrentSelection) {
      setFieldValue("rate_type", availableBillingRates[0].rate_type);
    }
  }, [selectedPosition, availableBillingRates, setFieldValue, values.rate_type, values.duration_value]);
  // Get user details for payment
  const [userDetails, setUserDetails] = useState(null);
  
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user_details') || 'null');
    setUserDetails(user);
  }, []);

  // Handle payment success callback
  const handlePaymentCallback = async (status) => {
    if (status === 'SUCCESS' && pendingAdData && paymentOrder) {
      // Create the ad after successful payment
      try {
        console.log("Creating ad after payment success...", pendingAdData);
        const response = await Post(apiURL + "/api/v1/ads", pendingAdData);
        console.log("Ad creation response:", response);
        
        if (response && response.data && response.data.status) {
          const newAd = response.data.data;
          setCreatedAd(newAd);
          toast.success("Ad created successfully after payment");
          
          // Link ad_id to payment record
          try {
            await Put(apiURL + `/api/v1/ads/payments/order/${paymentOrder.orderId}`, {
              ad_id: newAd.id
            });
            console.log("Payment linked to ad successfully");
          } catch (linkError) {
            console.error("Error linking ad to payment:", linkError);
            toast.warning("Ad created but failed to link payment record");
            // Non-critical error, continue
          }
          
          // Upload any pending files
          if (uploadingFiles.length > 0) {
            console.log("Uploading pending files...");
            await uploadPendingFiles(newAd.id);
          }
          
          // Fetch any existing uploads for this ad
          await fetchAdUploads(newAd.id);
          
          // Show success modal instead of navigating
          setCreatedAdId(newAd.id);
          setSuccessModalOpen(true);
        } else {
          const errorMsg = response?.data?.message || "Failed to create ad after payment";
          console.error("Ad creation failed:", errorMsg);
          toast.error(errorMsg);
          // Don't navigate if ad creation failed
        }
      } catch (error) {
        console.error("Error creating ad after payment:", error);
        toast.error("Failed to create ad after payment: " + (error?.message || "Unknown error"));
        // Don't navigate if ad creation failed
      }
    } else {
      console.warn("Payment callback called but missing data:", { status, hasPendingData: !!pendingAdData, hasPaymentOrder: !!paymentOrder });
    }
    
    // Clear pending data
    setDisplayRazorpay(false);
    // Don't clear pendingAdData and paymentOrder immediately - wait for navigation
  };

  // Fetch existing ad data for editing
  useEffect(() => {
    const fetchAdData = async () => {
      if (!isEditMode || !adId) return;
      
      setLoadingAd(true);
      try {
        // Fetch ad details - Note: The API returns data directly in response.data.data
        const adResponse = await GetAll(apiURL + `/api/v1/ads/${adId}`);
        if (adResponse && adResponse.data && adResponse.data.status) {
          const adData = adResponse.data.data;
          setExistingAd(adData);
          setCreatedAd(adData); // Set as createdAd so uploads work
          
          // Pre-populate form fields
          setFieldValue('name', adData.name || '');
          setFieldValue('developer_name', adData.developer_name || '');
          setFieldValue('description', adData.description || '');
          setFieldValue('position_id', adData.position_id || '');
          setFieldValue('layout_request_id', adData.layout_request_id || '');
          setFieldValue('rate_type', adData.rate_type || '');
          setFieldValue('duration_value', adData.duration_value || 1);
          
          // Set selected position for price display
          if (adData.position_id) {
            const position = adPositions.find(p => String(p.id) === String(adData.position_id));
            if (position) {
              setSelectedPosition(position);
            }
          }
          
          // Fetch existing uploads
          await fetchAdUploads(adId);
        } else {
          toast.error('Failed to load ad data');
          navigate('/dashboard/ads/create');
        }
      } catch (error) {
        console.error('Error fetching ad data:', error);
        toast.error('An error occurred while loading ad data');
        navigate('/dashboard/ads/create');
      } finally {
        setLoadingAd(false);
      }
    };
    
    if (isEditMode && adId && adPositions.length > 0 && layoutRequests.length > 0) {
      fetchAdData();
    }
  }, [isEditMode, adId, adPositions, layoutRequests, setFieldValue, navigate]);

  // Fetch existing uploads for an ad
  const fetchAdUploads = async (adId) => {
    if (!adId) return;
    try {
      const response = await GetAll(apiURL + `/api/v1/ads/${adId}/uploads`);
      if (response && response.data && response.data.status) {
        const uploadsData = Array.isArray(response.data.data) ? response.data.data : [];
        setUploads(uploadsData);
        console.log("Fetched existing uploads:", uploadsData);
      }
    } catch (error) {
      console.error("Error fetching ad uploads:", error);
    }
  };

  const isVideoFile = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv'];
    return videoExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const isImageFile = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const createFilePreview = (file) => {
    return new Promise((resolve) => {
      if (isImageFile(file.name)) {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ type: 'image', url: e.target.result, file });
        reader.readAsDataURL(file);
      } else if (isVideoFile(file.name)) {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ type: 'video', url: e.target.result, file });
        reader.readAsDataURL(file);
      } else {
        resolve({ type: 'document', url: null, file });
      }
    });
  };

  const uploadPendingFiles = async (adId) => {
    if (uploadingFiles.length === 0) return;
    
    toast.info(`Uploading ${uploadingFiles.length} file(s)...`);
    const uploadPromises = uploadingFiles.map(async (fileData) => {
      return await uploadFile(fileData.file, adId);
    });
    
    const results = await Promise.all(uploadPromises);
    const successCount = results.filter(r => r === true).length;
    
    if (successCount === uploadingFiles.length) {
      toast.success(`All ${successCount} file(s) uploaded successfully`);
    } else {
      toast.warning(`${successCount} of ${uploadingFiles.length} file(s) uploaded successfully`);
    }
    
    // Clear pending files after upload attempt
    setUploadingFiles([]);
    setFilePreviews([]);
  };

  const uploadFile = async (file, adId = null) => {
    // adId must be provided - either from parameter or from createdAd
    const targetAdId = adId || createdAd?.id;
    if (!targetAdId) {
      console.error("Cannot upload file: Ad ID is required");
      toast.error("Cannot upload file: Ad ID is required. Please create the ad first.");
      return false;
    }

    if (!file) {
      console.error("Cannot upload: File is missing");
      toast.error("Cannot upload: File is missing");
      return false;
    }

    console.log("Uploading file:", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      adId: targetAdId
    });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_name", file.name);
    formData.append("doc_type", "ad_asset");
    formData.append("ad_id", targetAdId);

    // Log FormData contents (for debugging)
    console.log("FormData contents:");
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ', pair[1]);
    }

    try {
      const uploadUrl = apiURL + `/api/v1/ads/upload_document`;
      console.log("Uploading to:", uploadUrl);
      
      const res = await Post(uploadUrl, formData);
      
      console.log("Upload response:", res);
      
      if (res && res.data) {
        if (res.data.status) {
          console.log("Upload successful:", res.data.data);
          // Add the new upload to the list
          setUploads((u) => {
            // Check if upload already exists to avoid duplicates
            const exists = u.some(item => item.id === res.data.data.id);
            return exists ? u : [...u, res.data.data];
          });
          setError({ type: "success", msg: "Uploaded successfully" });
          toast.success(`File "${file.name}" uploaded successfully`);
          return true;
        } else {
          const errorMsg = res.data.message || "Upload failed";
          console.error("Upload failed:", errorMsg);
          setError({ type: "error", msg: errorMsg });
          toast.error(errorMsg);
          return false;
        }
      } else {
        console.error("Invalid response format:", res);
        setError({ type: "error", msg: "Invalid response from server" });
        toast.error("Upload failed: Invalid response from server");
        return false;
      }
    } catch (error) {
      console.error("File upload error:", error);
      const errorMsg = error?.response?.data?.message || error?.message || "Upload failed";
      setError({ type: "error", msg: errorMsg });
      toast.error(`Upload failed: ${errorMsg}`);
      return false;
    }
  };

  const handleUpload = async (fileOrFiles) => {
    // Debug: Log what we're receiving
    console.log("handleUpload received:", fileOrFiles);
    console.log("Type:", typeof fileOrFiles, "Is Array:", Array.isArray(fileOrFiles));
    console.log("Is FileList:", fileOrFiles instanceof FileList);
    
    // Handle FileList, array, or single file
    let files = [];
    if (fileOrFiles instanceof FileList) {
      // Convert FileList to array
      files = Array.from(fileOrFiles);
    } else if (Array.isArray(fileOrFiles)) {
      files = fileOrFiles;
    } else if (fileOrFiles) {
      // Single file
      files = [fileOrFiles];
    }
    
    console.log("Converted files array:", files);
    
    // Process each file
    for (const file of files) {
      // Debug: Log file structure
      console.log("Processing file:", file);
      console.log("File name:", file?.name);
      console.log("File type:", file?.type);
      
      // Validate file object
      if (!file) {
        console.error("File object is null or undefined");
        setError({ type: "error", msg: "Invalid file: file object is missing" });
        toast.error("Invalid file: file object is missing");
        continue;
      }

      // Try to get file name from various possible properties
      let fileName = file.name || file.fileName || file.filename;
      
      // If still no name, try to generate one from type
      if (!fileName && file.type) {
        let extension = file.type.split('/')[1] || 'file';
        
        // Handle common image type variations
        if (extension === 'jpeg' || extension === 'jpg') {
          extension = 'jpg';
        }
        
        // Generate a timestamped filename
        const timestamp = Date.now();
        fileName = `upload_${timestamp}.${extension}`;
        console.warn("Generated filename:", fileName, "from type:", file.type);
      }
      
      // If still no name, skip this file
      if (!fileName) {
        console.error("File name is missing. File object:", file);
        setError({ type: "error", msg: `Invalid file: file name is missing for ${file.type || 'unknown file'}` });
        toast.error("Invalid file: file name is missing");
        continue;
      }
      
      // Update file object with name if it was missing
      if (!file.name && fileName) {
        file.name = fileName;
      }

      // Check file size (50MB for videos, 10MB for others)
      const isVideo = isVideoFile(file.name);
      const maxSizeMB = isVideo ? 50 : 10;
      const fileSizeMB = file.size / (1024 * 1024);
      
      if (fileSizeMB > maxSizeMB) {
        setError({ type: "error", msg: `File "${file.name}" exceeds ${maxSizeMB}MB limit` });
        toast.error(`File "${file.name}" exceeds ${maxSizeMB}MB limit`);
        continue;
      }

      // Create preview
      const preview = await createFilePreview(file);
      setFilePreviews((prev) => [...prev, preview]);
      setUploadingFiles((prev) => [...prev, { file, preview }]);

      // If ad is already created, upload immediately
      // Otherwise, files will be queued and uploaded after ad creation
      if (createdAd?.id) {
        const uploadSuccess = await uploadFile(file, createdAd.id);
        if (uploadSuccess) {
          // Remove from pending after successful upload
          setUploadingFiles((prev) => prev.filter((item) => item.file !== file));
          setFilePreviews((prev) => prev.filter((item) => item.file !== file));
        }
      }
      // If ad doesn't exist yet, file will remain in queue and upload after ad creation
    }
  };

  const removePendingFile = (index) => {
    setUploadingFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTypeError = () => {
    setError({ type: "error", msg: "Please upload correct format. Supported: Images (JPG, PNG, GIF, WEBP), Documents (PDF, DOC, DOCX), Videos (MP4, MOV, AVI, WMV, FLV, WEBM, MKV)" });
    toast.error("Invalid file format");
  };

  const handleSizeError = () => {
    setError({ type: "error", msg: "File size exceeds limit (10MB for images/documents, 50MB for videos)" });
    toast.error("File size exceeds limit");
  };

  if (loadingAd) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography variant="h6" color="text.secondary">
          Loading ad data...
        </Typography>
      </Box>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Card>
          <CardContent>
            <Typography align="center" variant="h4">
              {isEditMode ? 'Edit Ad' : 'Create Ad (Procurement)'}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Project Name"
                  {...getFieldProps("name")}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Developer Name"
                  {...getFieldProps("developer_name")}
                  error={Boolean(touched.developer_name && errors.developer_name)}
                  helperText={touched.developer_name && errors.developer_name}
                />
              </Grid>
              
              {/* Description Field - Only show if position_id is 4 */}
              {String(values.position_id) === '1' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    {...getFieldProps("description")}
                    error={Boolean(touched.description && errors.description)}
                    helperText={(touched.description && errors.description) || "Optional: Add a description for this ad"}
                    placeholder="Enter ad description (optional)"
                  />
                </Grid>
              )}
              
              {/* Project Dropdown (from Layout Requests) */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={Boolean(touched.layout_request_id && errors.layout_request_id)}>
                  <InputLabel>Select Layout</InputLabel>
                  <Select
                    value={values.layout_request_id}
                    onChange={(e) => {
                      setFieldValue("layout_request_id", e.target.value);
                    }}
                    label="Project (Layout Request)"
                    disabled={loadingProjects}
                  >
                    {layoutRequests.length > 0 ? (
                      layoutRequests.map((request) => (
                        <MenuItem key={request.id} value={request.id}>
                          {request.project_name || `Request #${request.id}`}
                          {request.status && ` - ${request.status}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        {loadingProjects ? "Loading..." : "No projects available"}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                {touched.layout_request_id && errors.layout_request_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.layout_request_id}
                  </Typography>
                )}
              </Grid>

              {/* Position Dropdown */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={Boolean(touched.position_id && errors.position_id)}>
                  <InputLabel>Ad Position</InputLabel>
                  <Select
                    value={values.position_id}
                    onChange={(e) => {
                      const positionId = e.target.value;
                      setFieldValue("position_id", positionId);
                      const position = adPositions.find((p) => String(p.id) === String(positionId));
                      setSelectedPosition(position);
                      const positionRates = normalizeRates(position?.rates);
                      if (positionRates.length > 0) {
                        setFieldValue("rate_type", positionRates[0].rate_type);
                      } else {
                        setFieldValue("rate_type", "");
                      }
                      setFieldValue("duration_value", 1);
                    }}
                    label="Ad Position"
                    disabled={loadingPositions}
                  >
                    {Array.isArray(adPositions) && adPositions.length > 0 ? (
                      adPositions.map((position) => {
                        return (
                          <MenuItem key={position.id} value={position.id}>
                            {position.name || position.position_name}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem disabled>
                        {loadingPositions ? "Loading positions..." : "No positions available"}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                {touched.position_id && errors.position_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.position_id}
                  </Typography>
                )}
              </Grid>

              {/* Billing Cycle Selector */}
              {selectedPosition && availableBillingRates.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={Boolean(touched.rate_type && errors.rate_type)}>
                    <InputLabel>Billing Cycle</InputLabel>
                    <Select
                      value={values.rate_type}
                      label="Billing Cycle"
                      onChange={(e) => setFieldValue("rate_type", e.target.value)}
                    >
                      {availableBillingRates.map((rate) => (
                        <MenuItem key={rate.rate_type} value={rate.rate_type}>
                          {RATE_TYPE_LABELS[rate.rate_type] || rate.rate_type}
                          {rate.amount ? ` - ₹${formatCurrency(rate.amount)}` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.rate_type && errors.rate_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.rate_type}
                    </Typography>
                  )}
                </Grid>
              )}

              {/* Duration Selection */}
              {selectedPosition && availableBillingRates.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Periods"
                    inputProps={{ min: 1 }}
                    {...getFieldProps("duration_value")}
                    error={Boolean(touched.duration_value && errors.duration_value)}
                    helperText={
                      (touched.duration_value && errors.duration_value) ||
                      `How many ${RATE_TYPE_LABELS[values.rate_type] || "periods"} do you need?`
                    }
                    disabled={!selectedBillingRate}
                  />
                </Grid>
              )}

              {/* Selected Rate Display */}
              {selectedBillingRate && (
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Selected Plan Rate ({RATE_TYPE_LABELS[selectedBillingRate.rate_type] || selectedBillingRate.rate_type})
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        ₹{formatCurrency(selectedBillingRate.amount) || "0"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        per {RATE_TYPE_LABELS[selectedBillingRate.rate_type] || selectedBillingRate.rate_type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Duration: {values.duration_value || 0} {RATE_TYPE_LABELS[selectedBillingRate.rate_type] || "periods"}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 0.5 }}>
                        Total: ₹{formatCurrency(calculatedTotalAmount) || "0"}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Upload Ad Asset (Image or Video)
              </Typography>
              <Box>
                <input
                  id="file_upload"
                  type="file"
                  style={{ display: "none" }}
                  multiple
                  accept={fileTypes.map(type => `.${type.toLowerCase()}`).join(",")}
                  onChange={(e) => handleUpload(e.target.files)}
                />
                <UploadBox
                  isUploaded={uploads.length > 0 || filePreviews.length > 0}
                  onClick={() => document.getElementById("file_upload").click()}
                  sx={{
                    border: error.type === "error" ? "2px dashed #d32f2f" : undefined,
                    backgroundColor: error.type === "error" ? "#ffebee" : undefined,
                  }}
                >
                  {filePreviews.length > 0 || uploads.length > 0 ? (
                    <>
                      <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 40 }} />
                      <Typography variant="body1" color="success.main" sx={{ mt: 1 }}>
                        {filePreviews.length + uploads.length} File(s) Ready
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click to upload more files
                      </Typography>
                    </>
                  ) : error.msg ? (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#d32f2f" }} />
                      <Typography variant="body1" color="error.main" sx={{ mt: 1 }}>
                        {error.msg}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click to try again
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                      <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                        Upload Ad Asset
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click to upload
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Images: JPG, PNG, GIF, WEBP (max 10MB)
                        <br />
                        Videos: MP4, MOV, AVI, WMV, FLV, WEBM, MKV (max 50MB)
                        <br />
                        Documents: PDF, DOC, DOCX (max 10MB)
                      </Typography>
                    </>
                  )}
                </UploadBox>
              </Box>
            </Box>

            {/* Pending Files Preview */}
            {filePreviews.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Files to Upload ({filePreviews.length})
                  </Typography>
                  {!createdAd?.id && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Files will be uploaded after ad is created
                    </Typography>
                  )}
                </Box>
                <Grid container spacing={2}>
                  {filePreviews.map((preview, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card sx={{ position: "relative" }}>
                        {preview.type === "image" && (
                          <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
                            <img
                              src={preview.url}
                              alt={preview.file.name}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "4px 4px 0 0",
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removePendingFile(index)}
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                backgroundColor: "error.main",
                                color: "white",
                                "&:hover": { backgroundColor: "error.dark" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        {preview.type === "video" && (
                          <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
                            <video
                              src={preview.url}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "4px 4px 0 0",
                              }}
                              controls
                            />
                            <IconButton
                              size="small"
                              onClick={() => removePendingFile(index)}
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                backgroundColor: "error.main",
                                color: "white",
                                "&:hover": { backgroundColor: "error.dark" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        {preview.type === "document" && (
                          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                            <DescriptionIcon sx={{ fontSize: 40, color: "primary.main" }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" noWrap>
                                {preview.file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(preview.file.size / (1024 * 1024)).toFixed(2)} MB
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removePendingFile(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Typography variant="caption" noWrap>
                            {preview.file.name}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {(preview.file.size / (1024 * 1024)).toFixed(2)} MB
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Uploaded Files */}
            {uploads.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Uploaded Files ({uploads.length})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {uploads.map((u, idx) => {
                    const getIcon = () => {
                      if (isVideoFile(u.file_name)) return <VideoFileIcon />;
                      if (isImageFile(u.file_name)) return <ImageIcon />;
                      return <DescriptionIcon />;
                    };
                    return (
                      <Chip
                        key={idx}
                        icon={getIcon()}
                        label={u.file_name}
                        component="a"
                        href={u.filepaths3}
                        target="_blank"
                        rel="noreferrer"
                        clickable
                        color="primary"
                        variant="outlined"
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}
          </CardContent>
          <CardActions sx={{ display: "flex", justifyContent: "flex-end" }}>
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{ backgroundColor: "#3f51b5", "&:hover": { color: "#ffff", backgroundColor: "#3f51b5" } }}
            >
              {isEditMode ? 'Update' : 'Pay'}
            </LoadingButton>
          </CardActions>
        </Card>
      </Form>
      {displayRazorpay && paymentOrder && userDetails && (
        <RenderRazorpay
          amount={paymentOrder.amount}
          currency={paymentOrder.currency}
          orderId={paymentOrder.orderId}
          keyId={'rzp_live_T0Fj5jdM5C2Q6F'}
          keySecret={''}
          email={userDetails.email || ''}
          phone={userDetails.phone || ''}
          page={'ad_payment'}
          handleCallBack={handlePaymentCallback}
        />
      )}

      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 64, 
              color: 'success.main', 
              mb: 2,
              display: 'block',
              mx: 'auto'
            }} 
          />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Payment Successful!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
            Your ad has been created successfully after payment.
          </Typography>
          {createdAdId && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Ad ID: {createdAdId}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessModalOpen(false);
              navigate('/dashboard/procurement/ads-requests');
            }}
            sx={{ minWidth: 200 }}
          >
            Return to Ads List
          </Button>
        </DialogActions>
      </Dialog>
    </FormikProvider>
  );
};

export default AdsCreate;